import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils/formatters';

interface AllocationWarningDialogProps {
  type: 'disputed' | 'unapplied' | 'promise_ignored';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: {
    transactionNumber?: string;
    unappliedAmount?: number;
  };
  onContinue: () => void;
  onAutoAllocate?: () => void;
}

export function AllocationWarningDialog({
  type,
  open,
  onOpenChange,
  data = {},
  onContinue,
  onAutoAllocate,
}: AllocationWarningDialogProps) {
  const handleContinue = () => {
    onContinue();
    onOpenChange(false);
  };

  const handleAutoAllocate = () => {
    if (onAutoAllocate) {
      onAutoAllocate();
    }
    onOpenChange(false);
  };

  const renderContent = () => {
    switch (type) {
      case 'disputed':
        return (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>⚠️ Disputed Transaction</AlertDialogTitle>
              <AlertDialogDescription>
                Transaction {data.transactionNumber} is currently disputed.
                Allocating payment may affect the dispute resolution.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleContinue}>
                Continue Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        );

      case 'unapplied':
        return (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>⚠️ Unapplied Balance</AlertDialogTitle>
              <AlertDialogDescription>
                You have {formatCurrency(data.unappliedAmount || 0)} unapplied.
                Would you like to auto-allocate the remaining amount?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleContinue}>
                Save with Unapplied Balance
              </AlertDialogCancel>
              {onAutoAllocate && (
                <Button variant="default" onClick={handleAutoAllocate}>
                  Auto-Allocate Remaining
                </Button>
              )}
            </AlertDialogFooter>
          </>
        );

      case 'promise_ignored':
        return (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>⚠️ Promise Ignored</AlertDialogTitle>
              <AlertDialogDescription>
                You ignored the Promise to Pay for this receipt.
                The promise will remain in "Pending" status.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction onClick={handleContinue}>
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        {renderContent()}
      </AlertDialogContent>
    </AlertDialog>
  );
}
